import type {
  AggregationsMap,
  AggregationEntry,
} from "~/models/Content/SearchResult"
import type {
  Aggregation as ApiAggregation,
  AggregationEntry as ApiAggregationEntry,
} from "~/models/Content/Response"

/**
 * Formats the aggregations from the API response into the application's AggregationsMap
 * @param aggregations Array of API aggregation objects
 * @returns Formatted object of aggregation entries mapped by aggregation name
 */
export const formatAggregations = (
  aggregations?: ApiAggregation[],
): AggregationsMap | undefined => {
  if (!aggregations || aggregations.length === 0) {
    return undefined
  }

  const aggregationsMap: AggregationsMap = {}

  aggregations.forEach(agg => {
    if (agg.name) {
      aggregationsMap[agg.name] = formatAggregationEntries(
        agg.entries,
        agg.name,
      )
    }
  })

  return aggregationsMap
}

/**
 * Formats the aggregation entries from the API response into the application's AggregationEntry model
 * @param entries Array of API aggregation entry objects
 * @param aggregationName The name of the parent aggregation
 * @returns Formatted array of AggregationEntry objects
 */
export const formatAggregationEntries = (
  entries: ApiAggregationEntry[],
  aggregationName?: string,
): AggregationEntry[] => {
  if (!entries || entries.length === 0) {
    return []
  }

  return entries.map(entry => ({
    key: formatAggregationKey(entry.key, aggregationName),
    count: entry.count,
  }))
}

/**
 * Formats the aggregation key from the API response
 * Keys can be simple strings or complex objects (like Location or ObjectState)
 * For "parentLocationId", extracts only the ID from the Location object
 *
 * @param key The key value from the API response
 * @param aggregationName The name of the parent aggregation
 * @returns Formatted key as string or object
 */
export const formatAggregationKey = (
  key: unknown,
  aggregationName?: string,
): string | { [key: string]: any } => {
  if (typeof key === "string") {
    return key
  }

  if (key && typeof key === "object") {
    // Special handling for parentLocationId - extract only the ID
    if (aggregationName === "parentLocationId" && "Location" in key) {
      const location = key.Location
      if (
        location &&
        typeof location === "object" &&
        "id" in location
      ) {
        const id = location.id
        if (typeof id === "string" || typeof id === "number") {
          return String(id)
        }
      }
    }

    // Default handling for complex keys
    if ("Location" in key) {
      return { Location: key.Location }
    }
    if ("ObjectState" in key) {
      return { ObjectState: key.ObjectState }
    }
  }

  return String(key)
}
